/**
 * ExpenseList Component
 * Filterable list of expenses for a property or unit
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { expensesApi, expenseCategoriesApi } from '../../lib/api';
import type { Expense, ExpenseCategory } from '../../types/expense.types';
import { formatAmount } from '../../utils/expense.utils';
import { formatCalendarDate, todayInTimezone, asCalendarDateString } from '@resida/shared';

function getDatePresetRange(preset: string, timezone: string): { start: string; end: string } {
  const todayStr = todayInTimezone(timezone);
  const parts = todayStr.split('-').map(Number);
  const year = parts[0]!;
  const end = todayStr;
  const subtractDays = (days: number): string => {
    const d = new Date(parts[0]!, parts[1]! - 1, parts[2]!);
    d.setDate(d.getDate() - days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  switch (preset) {
    case 'last30': return { start: subtractDays(30), end };
    case 'last60': return { start: subtractDays(60), end };
    case 'last90': return { start: subtractDays(90), end };
    case 'ytd': return { start: `${year}-01-01`, end };
    case 'lastYear': return { start: `${year - 1}-01-01`, end: `${year - 1}-12-31` };
    default: return { start: '', end: '' };
  }
}

interface ExpenseListProps {
  propertyId?: string;
  unitId?: string;
  /** IANA timezone string for the property. Defaults to America/New_York if not provided. */
  timezone?: string;
}

function SourceTypeBadge({ sourceType }: { sourceType: Expense['sourceType'] }) {
  if (sourceType === 'EMAIL') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning/10 text-warning">
        Email
      </span>
    );
  }
  if (sourceType === 'UPLOAD') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
        Upload
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
      Manual
    </span>
  );
}

export function ExpenseList({ propertyId, unitId, timezone = 'America/New_York' }: ExpenseListProps) {
  const navigate = useNavigate();

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [datePreset, setDatePreset] = useState('last30');
  const last30Range = getDatePresetRange('last30', timezone);
  const [filterStartDate, setFilterStartDate] = useState(last30Range.start);
  const [filterEndDate, setFilterEndDate] = useState(last30Range.end);
  const [filterCategory, setFilterCategory] = useState('');

  // Delete confirm
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const effectivePropertyId = propertyId ?? '';

  const loadData = useCallback(async () => {
    if (!effectivePropertyId) return;

    try {
      setLoading(true);
      setError(null);

      const [expensesResult, categoriesResult] = await Promise.all([
        expensesApi.list({
          propertyId: effectivePropertyId,
          unitId,
          startDate: filterStartDate || undefined,
          endDate: filterEndDate || undefined,
          categoryId: filterCategory || undefined,
          status: 'CONFIRMED',
        }),
        expenseCategoriesApi.list(),
      ]);

      setExpenses(expensesResult.expenses);
      setCategories(categoriesResult.categories);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [effectivePropertyId, unitId, filterStartDate, filterEndDate, filterCategory]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async (id: string) => {
    setDeleteError(null);
    try {
      await expensesApi.delete(id);
      setDeletingId(null);
      await loadData();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete expense');
    }
  };

  const getCategoryName = (id: string | null): string => {
    if (!id) return '—';
    const cat = categories.find((c) => c.id === id);
    return cat ? cat.name : id;
  };

  const sortedCategories = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);

  if (!effectivePropertyId) {
    return (
      <div data-testid="expense-list" className="py-8 text-center text-muted-foreground text-sm">
        No property selected.
      </div>
    );
  }

  return (
    <div data-testid="expense-list">
      {/* Filter Controls */}
      <div className="flex gap-3 flex-wrap items-end mb-4 p-4 bg-accent/50 rounded-lg border border-border">
        <div className="flex-1 min-w-[140px]">
          <label
            htmlFor="expense-filter-date-preset"
            className="block text-xs font-medium text-muted-foreground mb-1"
          >
            Date Range
          </label>
          <select
            id="expense-filter-date-preset"
            value={datePreset}
            onChange={(e) => {
              const preset = e.target.value;
              setDatePreset(preset);
              if (preset !== 'custom') {
                const range = getDatePresetRange(preset, timezone);
                setFilterStartDate(range.start);
                setFilterEndDate(range.end);
              }
            }}
            className="w-full px-2 py-1 text-[13px] border border-input rounded-md bg-card outline-none min-h-[44px]"
            data-testid="expense-filter-date-preset"
            aria-label="Select date range preset"
          >
            <option value="last30">Last 30 Days</option>
            <option value="last60">Last 60 Days</option>
            <option value="last90">Last 90 Days</option>
            <option value="ytd">Year to Date</option>
            <option value="lastYear">Last Year</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        {datePreset === 'custom' && (
          <>
            <div className="flex-1 min-w-[140px]">
              <label
                htmlFor="expense-filter-start-date"
                className="block text-xs font-medium text-muted-foreground mb-1"
              >
                From
              </label>
              <input
                id="expense-filter-start-date"
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
                className="w-full px-2 py-1 text-[13px] border border-input rounded-md outline-none min-h-[44px]"
                data-testid="expense-filter-start-date"
                aria-label="Filter start date"
              />
            </div>

            <div className="flex-1 min-w-[140px]">
              <label
                htmlFor="expense-filter-end-date"
                className="block text-xs font-medium text-muted-foreground mb-1"
              >
                To
              </label>
              <input
                id="expense-filter-end-date"
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
                className="w-full px-2 py-1 text-[13px] border border-input rounded-md outline-none min-h-[44px]"
                data-testid="expense-filter-end-date"
                aria-label="Filter end date"
              />
            </div>
          </>
        )}

        <div className="flex-[2] min-w-[180px]">
          <label
            htmlFor="expense-filter-category"
            className="block text-xs font-medium text-muted-foreground mb-1"
          >
            Category
          </label>
          <select
            id="expense-filter-category"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="w-full px-2 py-1 text-[13px] border border-input rounded-md bg-card outline-none min-h-[44px]"
            data-testid="expense-filter-category"
            aria-label="Filter by category"
          >
            <option value="">All Categories</option>
            {sortedCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="shrink-0">
          {(datePreset !== 'last30' || filterCategory) && (
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground underline min-h-[44px] px-2"
              onClick={() => {
                const range = getDatePresetRange('last30', timezone);
                setDatePreset('last30');
                setFilterStartDate(range.start);
                setFilterEndDate(range.end);
                setFilterCategory('');
              }}
              aria-label="Clear all filters"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Header row */}
      <div className="flex justify-between items-center mb-4">
        <p className="m-0 text-[13px] text-muted-foreground">
          {loading ? 'Loading...' : `${expenses.length} expense${expenses.length !== 1 ? 's' : ''}`}
        </p>
      </div>

      {error && (
        <div
          role="alert"
          className="p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm"
        >
          {error}
        </div>
      )}

      {deleteError && (
        <div
          role="alert"
          className="p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm"
        >
          {deleteError}
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center text-muted-foreground text-sm">
          Loading expenses...
        </div>
      ) : expenses.length === 0 ? (
        <div
          className="py-10 px-6 text-center text-muted-foreground bg-accent/50 rounded-lg border border-border"
          data-testid="expense-list-empty"
        >
          <p className="m-0 text-[15px]">No expenses found. Use the button above to add one.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2" role="list">
          {expenses.map((expense) => (
            <div
              key={expense.id}
              role="listitem"
              className="bg-card rounded-lg border border-border"
              data-testid={`expense-list-row-${expense.id}`}
            >
              {deletingId === expense.id ? (
                <div className="p-4">
                  <p className="m-0 mb-3 text-sm text-foreground">
                    Delete <strong>{expense.description}</strong> ({formatAmount(expense.amount)})? This cannot be undone.
                  </p>
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      className="text-sm text-muted-foreground hover:text-foreground underline min-h-[44px] px-2"
                      onClick={() => { setDeletingId(null); setDeleteError(null); }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm text-destructive-foreground bg-destructive rounded-md hover:bg-destructive/90 min-h-[44px]"
                      onClick={() => handleDelete(expense.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  {/* Date */}
                  <div className="shrink-0 w-[90px]">
                    <span className="text-[13px] text-muted-foreground">
                      {formatCalendarDate(asCalendarDateString(expense.expenseDate))}
                    </span>
                  </div>

                  {/* Description + vendor */}
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/expenses/${expense.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate(`/expenses/${expense.id}`);
                      }
                    }}
                    aria-label={`View expense: ${expense.description}`}
                  >
                    <p className="m-0 text-sm font-medium text-foreground truncate">
                      {expense.description}
                    </p>
                    {expense.vendorName && (
                      <p className="m-0 text-xs text-muted-foreground">
                        {expense.vendorName}
                      </p>
                    )}
                  </div>

                  {/* Category — hidden on small screens via utility class */}
                  <div className="shrink-0 w-[140px] hidden expense-list-category-col">
                    <span className="text-xs text-muted-foreground">
                      {getCategoryName(expense.irsCategoryId)}
                    </span>
                  </div>

                  {/* Source badge */}
                  <div className="shrink-0">
                    <SourceTypeBadge sourceType={expense.sourceType} />
                  </div>

                  {/* Amount */}
                  <div className="shrink-0 text-right min-w-[80px]">
                    <span className="text-[15px] font-semibold text-foreground">
                      {formatAmount(expense.amount)}
                    </span>
                  </div>

                  {/* Delete button */}
                  <div className="shrink-0">
                    <button
                      type="button"
                      className="px-2 py-1 text-xs text-destructive-foreground bg-destructive rounded-md hover:bg-destructive/90 min-h-[44px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingId(expense.id);
                        setDeleteError(null);
                      }}
                      data-testid={`expense-delete-${expense.id}`}
                      aria-label={`Delete expense: ${expense.description}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ExpenseList;
